import { useState, useEffect } from 'react';
import api from '../../utils/api';

// Renders one interactive engagement question inside a course slide.
// The student picks an option, gets instant correct/incorrect feedback,
// and can then advance. Answer is persisted to user_interactive_responses.
export default function InteractiveQuestion({ questionId, userCourseId, onAnswered }) {
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState(null);
  const [options, setOptions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [submittedResult, setSubmittedResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Fetch a single question by ID, we piggyback on the section list endpoint
    // by asking for the interactive question data via a dedicated fetch below.
    // We fetch fresh each time so answer options shuffle per view.
    setLoading(true);
    setSelectedId(null);
    setSubmittedResult(null);
    api.get(`/interactive/question/${questionId}`)
      .then(res => {
        setQuestion(res.data.question);
        setOptions(res.data.options || []);
        if (res.data.previousResult) {
          // Student already answered — restore that state so they can see feedback
          setSelectedId(res.data.previousResult.selectedOptionId);
          setSubmittedResult(res.data.previousResult);
          // Notify parent so the Next button unlocks immediately on revisit
          if (onAnswered) onAnswered(res.data.previousResult);
        }
      })
      .catch(err => console.error('Load interactive question failed:', err))
      .finally(() => setLoading(false));
  }, [questionId]);

  const handleSubmit = async () => {
    if (!selectedId || submitting || submittedResult) return;
    setSubmitting(true);
    try {
      const res = await api.post('/interactive/answer', {
        questionId,
        selectedOptionId: selectedId,
        userCourseId,
      });
      setSubmittedResult(res.data);
      if (onAnswered) onAnswered(res.data);
    } catch (err) {
      alert('Failed to submit answer: ' + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: 'var(--slate-400)' }}>Loading question…</div>;
  if (!question) return null;

  const isAnswered = !!submittedResult;
  const correctOptionId = isAnswered ? options.find(o => o.isCorrect || o.is_correct)?.id : null;

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <span style={styles.tag}>💡 Engagement Check</span>
        {isAnswered && (
          <span style={{ ...styles.resultBadge, ...(submittedResult.isCorrect ? styles.correctBadge : styles.incorrectBadge) }}>
            {submittedResult.isCorrect ? '✓ Correct' : '✗ Not quite'}
          </span>
        )}
      </div>

      <p style={styles.questionText}>{question.question_text || question.questionText}</p>

      <div style={styles.options}>
        {options.map(opt => {
          const isSelected = selectedId === opt.id;
          const isCorrectOption = isAnswered && (opt.isCorrect || opt.is_correct);
          const isWrongPick = isAnswered && isSelected && !isCorrectOption;
          let optStyle = { ...styles.option };
          if (isAnswered) {
            if (isCorrectOption) optStyle = { ...optStyle, ...styles.optionCorrect };
            else if (isWrongPick) optStyle = { ...optStyle, ...styles.optionWrong };
            else optStyle = { ...optStyle, ...styles.optionMuted };
          } else if (isSelected) {
            optStyle = { ...optStyle, ...styles.optionSelected };
          }
          return (
            <label key={opt.id} style={optStyle}>
              <input
                type="radio"
                name={`iq-${questionId}`}
                value={opt.id}
                checked={isSelected}
                disabled={isAnswered}
                onChange={() => setSelectedId(opt.id)}
                style={{ accentColor: 'var(--teal-400)' }}
              />
              <span>{opt.text || opt.option_text}</span>
              {isAnswered && isCorrectOption && <span style={styles.mark}>✓</span>}
              {isAnswered && isWrongPick && <span style={{ ...styles.mark, color: '#f87171' }}>✗</span>}
            </label>
          );
        })}
      </div>

      {!isAnswered ? (
        <button
          onClick={handleSubmit}
          disabled={!selectedId || submitting}
          style={{ ...styles.submitBtn, ...((!selectedId || submitting) ? styles.submitBtnDisabled : {}) }}
        >
          {submitting ? 'Submitting…' : 'Submit answer'}
        </button>
      ) : (
        submittedResult.explanation && (
          <div style={styles.explanation}>
            <strong>Explanation:</strong> {submittedResult.explanation}
          </div>
        )
      )}
    </div>
  );
}

const styles = {
  wrap: { background: 'rgba(255,255,255,0.03)', border: '1px solid var(--slate-700)', borderRadius: 12, padding: '28px 32px', maxWidth: 720, margin: '0 auto' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 },
  tag: { display: 'inline-block', fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#d4b896', background: 'rgba(179,158,137,0.15)', padding: '4px 12px', borderRadius: 999 },
  resultBadge: { fontSize: 13, fontWeight: 700, padding: '4px 12px', borderRadius: 999 },
  correctBadge: { color: '#4ade80', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.35)' },
  incorrectBadge: { color: '#f87171', background: 'rgba(248,113,113,0.15)', border: '1px solid rgba(248,113,113,0.35)' },
  questionText: { fontSize: 18, lineHeight: 1.5, color: 'white', marginBottom: 20 },
  options: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 },
  option: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', border: '1.5px solid var(--slate-600)', borderRadius: 8, cursor: 'pointer', color: 'var(--slate-300)', transition: 'all 0.15s' },
  optionSelected: { borderColor: 'var(--teal-400)', background: 'rgba(45,212,191,0.08)', color: 'white' },
  optionCorrect: { borderColor: 'rgba(34,197,94,0.5)', background: 'rgba(34,197,94,0.12)', color: '#86efac' },
  optionWrong: { borderColor: 'rgba(248,113,113,0.5)', background: 'rgba(248,113,113,0.12)', color: '#fca5a5' },
  optionMuted: { opacity: 0.55 },
  mark: { marginLeft: 'auto', fontWeight: 700, color: '#4ade80' },
  submitBtn: { display: 'block', width: '100%', padding: '12px 24px', background: '#b39e89', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  submitBtnDisabled: { background: 'var(--slate-600)', cursor: 'not-allowed' },
  explanation: { padding: '12px 16px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)', borderRadius: 8, fontSize: 14, color: '#93c5fd', lineHeight: 1.5 },
};
